import React from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import * as Lucide from 'lucide-react';
import { Plus, Trash, PencilSimple, ArrowUp, ArrowDown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon names allowed for categories. Users pick from this curated list.
const ICON_CHOICES = [
  'Sparkles', 'Users', 'Wrench', 'ArrowUpDown', 'Trees', 'Bug', 'Hammer',
  'SearchCheck', 'ShieldCheck', 'Receipt', 'CircleDollarSign', 'Circle',
  'Calendar', 'Clock', 'Star', 'Flag', 'Home', 'Building', 'Building2',
  'Bell', 'Zap', 'ListChecks', 'User', 'Briefcase', 'Phone', 'Car',
  'Cake', 'StickyNote', 'PhoneCall', 'MapPin', 'Package',
];

const PRESET_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#22c55e',
  '#ef4444', '#f97316', '#06b6d4', '#0ea5e9', '#eab308',
  '#059669', '#64748b', '#ec4899', '#a855f7', '#14b8a6',
];

export function CategoryIcon({ name, size = 14, className = '' }) {
  const Cmp = Lucide[name] || Lucide.Circle;
  return <Cmp size={size} className={className} />;
}

export default function CategoryManager({
  open, onClose, onChanged, language = 'el',
  basePath = 'task-categories',   // API path segment
  idField = 'category_id',        // primary key name on the doc
  title,                          // Dialog title override
}) {
  const [cats, setCats] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/${basePath}`);
      setCats(r.data || []);
    } catch (_) {
      toast.error(language === 'el' ? 'Αποτυχία φόρτωσης' : 'Failed to load');
    } finally { setLoading(false); }
  }, [language, basePath]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    if (!editing) return;
    const payload = {
      name: editing.name?.trim(),
      color: editing.color || '#64748b',
      icon: editing.icon || 'Circle',
      active: editing.active !== false,
    };
    if (!payload.name) { toast.error(language === 'el' ? 'Απαιτείται όνομα' : 'Name required'); return; }
    try {
      const id = editing[idField];
      if (id) {
        await axios.put(`${API}/${basePath}/${id}`, payload);
        toast.success(language === 'el' ? 'Αποθηκεύτηκε' : 'Saved');
      } else {
        await axios.post(`${API}/${basePath}`, payload);
        toast.success(language === 'el' ? 'Δημιουργήθηκε' : 'Created');
      }
      setEditing(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm(language === 'el' ? 'Διαγραφή κατηγορίας;' : 'Delete this category?')) return;
    try {
      await axios.delete(`${API}/${basePath}/${id}`);
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const reorder = async (idx, dir) => {
    const next = [...cats];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setCats(next);
    try {
      await axios.post(`${API}/${basePath}/reorder`, {
        order: next.map(c => c[idField]),
      });
      onChanged && onChanged();
    } catch (_) {
      toast.error(language === 'el' ? 'Αποτυχία σειράς' : 'Reorder failed');
      await load();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[640px] rounded-none max-h-[85vh] overflow-y-auto" data-testid="category-manager">
        <DialogHeader>
          <DialogTitle>
            {title || (language === 'el' ? 'Διαχείριση Κατηγοριών' : 'Manage Categories')}
          </DialogTitle>
        </DialogHeader>

        {editing ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{language === 'el' ? 'Όνομα' : 'Name'}</Label>
              <Input
                value={editing.name || ''}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="mt-1 rounded-none"
                data-testid="cat-edit-name"
              />
            </div>
            <div>
              <Label className="text-xs">{language === 'el' ? 'Χρώμα' : 'Colour'}</Label>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setEditing({ ...editing, color: c })}
                    className={`w-6 h-6 border-2 ${editing.color === c ? 'border-zinc-900' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                    data-testid={`cat-color-${c}`}
                  />
                ))}
                <Input
                  type="color"
                  value={editing.color || '#64748b'}
                  onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                  className="w-16 h-8 p-0 border border-zinc-300"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">{language === 'el' ? 'Εικονίδιο' : 'Icon'}</Label>
              <div className="flex items-center gap-2 flex-wrap mt-1 max-h-32 overflow-y-auto">
                {ICON_CHOICES.map(ic => (
                  <button
                    key={ic}
                    type="button"
                    onClick={() => setEditing({ ...editing, icon: ic })}
                    className={`p-1.5 border ${editing.icon === ic ? 'border-zinc-900 bg-zinc-100' : 'border-zinc-200'}`}
                    title={ic}
                    data-testid={`cat-icon-${ic}`}
                  >
                    <CategoryIcon name={ic} size={16} />
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.active !== false}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                data-testid="cat-active"
              />
              <span>{language === 'el' ? 'Ενεργή' : 'Active'}</span>
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none">
                {language === 'el' ? 'Άκυρο' : 'Cancel'}
              </Button>
              <Button onClick={save} className="rounded-none bg-zinc-900 text-white hover:bg-zinc-800" data-testid="cat-save-btn">
                {language === 'el' ? 'Αποθήκευση' : 'Save'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => setEditing({ name: '', color: '#3b82f6', icon: 'Circle', active: true })}
                className="rounded-none bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="cat-new-btn"
              >
                <Plus size={13} className="mr-1" /> {language === 'el' ? 'Νέα' : 'New'}
              </Button>
            </div>
            {loading ? (
              <div className="text-sm text-zinc-400 py-6 text-center">…</div>
            ) : (
              <div className="space-y-1">
                {cats.map((c, idx) => {
                  const cid = c[idField];
                  return (
                  <div key={cid}
                        className={`flex items-center gap-2 border border-zinc-200 px-2 py-1.5 ${c.active === false ? 'opacity-50' : ''}`}
                        data-testid={`cat-row-${cid}`}>
                    <div className="w-4 h-4" style={{ backgroundColor: c.color || '#64748b' }} />
                    <CategoryIcon name={c.icon || 'Circle'} size={16} className="text-zinc-600" />
                    <span className="flex-1 text-sm">{c.name}</span>
                    {c.is_default && (
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400">
                        {language === 'el' ? 'Προεπιλογή' : 'Default'}
                      </span>
                    )}
                    <button type="button" onClick={() => reorder(idx, -1)} className="p-1 text-zinc-500 hover:text-zinc-900" data-testid={`cat-up-${cid}`}>
                      <ArrowUp size={12} />
                    </button>
                    <button type="button" onClick={() => reorder(idx, 1)} className="p-1 text-zinc-500 hover:text-zinc-900" data-testid={`cat-down-${cid}`}>
                      <ArrowDown size={12} />
                    </button>
                    <button type="button" onClick={() => setEditing({ ...c })} className="p-1 text-blue-600 hover:text-blue-800" data-testid={`cat-edit-${cid}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button type="button" onClick={() => remove(cid)} className="p-1 text-red-500 hover:text-red-700" data-testid={`cat-delete-${cid}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="rounded-none">
                {language === 'el' ? 'Κλείσιμο' : 'Close'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
